import axios from 'axios';
import config from '../config';
import { getBearerToken } from '../util/cookie';

// ================ Action types ================ //

export const TESTIMONIALS_REQUEST = 'app/CoachSaaS/TESTIMONIALS_REQUEST';
export const TESTIMONIALS_SUCCESS = 'app/CoachSaaS/TESTIMONIALS_SUCCESS';
export const TESTIMONIALS_ERROR = 'app/CoachSaaS/TESTIMONIALS_ERROR';

// ================ Reducer ================ //

const initialState = {
  testimonials: [],
  testimonialsFetching: false,
  testimonialsError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case TESTIMONIALS_REQUEST:
      return { ...state, testimonialsFetching: true, testimonialsError: null };
    case TESTIMONIALS_SUCCESS:
      return { ...state, testimonialsFetching: false, testimonials: payload };
    case TESTIMONIALS_ERROR:
      return { ...state, testimonialsFetching: false, testimonialsError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const testimonialsRequest = () => ({ type: TESTIMONIALS_REQUEST });
export const testimonialsSuccess = (data) => ({ type: TESTIMONIALS_SUCCESS, payload: data });
export const testimonialsError = (err) => ({ type: TESTIMONIALS_ERROR, payload: err });

// ================ Thunks ================ //

// Submit a new testimonial. `payload` is the validated form data the
// coach typed. Returns the axios promise so the form can show inline
// errors based on the backend response.
export const submitTestimonial = (payload) => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}testimonials/submit`,
    { payload },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};

// Fetch the coach's own list of testimonials. Populates Redux state.
export const fetchMyTestimonials = () => async (dispatch, getState, sdk) => {
  dispatch(testimonialsRequest());
  try {
    const res = await axios.post(
      `${config.coachSaaSService}testimonials/list`,
      {},
      {
        headers: {
          'content-type': 'application/json',
          Authorization: await getBearerToken(sdk),
        },
      }
    );
    dispatch(testimonialsSuccess((res.data && res.data.testimonials) || []));
    return res.data;
  } catch (e) {
    dispatch(testimonialsError(e));
    throw e;
  }
};

// Delete one of the coach's testimonials by id.
export const deleteTestimonial = (testimonialId) => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}testimonials/delete`,
    { testimonialId },
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};


// Read-only preview of a pending testimonial by confirmation token. No
// auth — the referee may not be logged in yet when they first open the
// email link. Never mutates the record (see backend testimonialsAccess.preview).
export const previewTestimonial = (token) => async () => {
  return axios.get(`${config.coachSaaSService}testimonials/preview`, {
    params: { t: token },
  });
};

// Referee has (optionally) rewritten the testimonial text. This single
// call both saves the edit and confirms the testimonial. No auth — same
// token-possession trust model as previewTestimonial/confirm above, since
// the referee may not have (or want) a Viveka account.
export const confirmTestimonialWithEdit = (token, body) => async () => {
  return axios.post(
    `${config.coachSaaSService}testimonials/edit-confirm`,
    { t: token, body },
    {
      headers: {
        'content-type': 'application/json',
      },
    }
  );
};

// Pending testimonial requests addressed to the current user (used
// by the participant dashboard nudge). Returns whatever pending
// testimonials a coach has submitted with this user's email as
// the referee.
export const fetchTestimonialRequestsForMe = () => async (dispatch, getState, sdk) => {
  return axios.post(
    `${config.coachSaaSService}testimonials/for-me`,
    {},
    {
      headers: {
        'content-type': 'application/json',
        Authorization: await getBearerToken(sdk),
      },
    }
  );
};
